import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trophy, Medal, Loader2, ChevronDown, ChevronUp, User, Clock, Flame, Star } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import GamificationLeaderboard from './GamificationLeaderboard';

import { API } from '../config/api';

// Medal colors
const MEDAL_COLORS = {
  1: { bg: 'bg-yellow-500/20', border: 'border-yellow-500', text: 'text-yellow-400', label: '🥇' },
  2: { bg: 'bg-gray-300/20', border: 'border-gray-400', text: 'text-gray-300', label: '🥈' },
  3: { bg: 'bg-amber-600/20', border: 'border-amber-600', text: 'text-amber-500', label: '🥉' }
};

export default function Leaderboards({ childId, childAge, isCoach = false }) {
  const [loading, setLoading] = useState(true);
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState('');
  const [selectedAge, setSelectedAge] = useState(childAge?.toString() || '');
  const [leaderboard, setLeaderboard] = useState(null);
  const [childRanking, setChildRanking] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [viewMode, setViewMode] = useState('performance'); // 'performance' or 'points'

  const loadLeaderboard = useCallback(async () => {
    try {
      const age = selectedAge ? parseInt(selectedAge) : null;
      const url = age 
        ? `${API}/leaderboards/${selectedTest}?age=${age}&limit=10`
        : `${API}/leaderboards/${selectedTest}?limit=10`;
      const response = await axios.get(url);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    }
  }, [selectedTest, selectedAge]);

  const loadChildRanking = useCallback(async () => {
    if (!childId) return;
    try {
      const response = await axios.get(`${API}/leaderboards/${selectedTest}/child/${childId}`);
      setChildRanking(response.data);
    } catch (error) {
      console.error('Failed to load child ranking:', error);
    }
  }, [childId, selectedTest]);

  useEffect(() => {
    loadTests();
  }, []);

  useEffect(() => {
    if (selectedTest && (selectedAge || isCoach)) {
      loadLeaderboard();
    }
  }, [selectedTest, selectedAge, isCoach, loadLeaderboard]);

  useEffect(() => {
    if (childId && selectedTest) {
      loadChildRanking();
    }
  }, [childId, selectedTest, loadChildRanking]);

  const loadTests = async () => {
    try {
      const response = await axios.get(`${API}/leaderboards/tests`);
      setTests(response.data.tests || []);
      if (response.data.tests?.length > 0) {
        setSelectedTest(response.data.tests[0].id);
      }
    } catch (error) {
      console.error('Failed to load tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatResult = (value, unit) => {
    if (unit === 's') return `${value.toFixed(2)}s`;
    if (unit === 'cm') return `${value.toFixed(1)}cm`;
    if (unit === 'm') return `${value.toFixed(2)}m`;
    return `${value}${unit ? ` ${unit}` : ''}`;
  };

  const getPositionStyle = (rank) => {
    return MEDAL_COLORS[rank] || { bg: 'bg-zinc-800/50', border: 'border-zinc-700', text: 'text-zinc-400', label: rank };
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden" data-testid="leaderboards">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Trophy className="h-5 w-5 text-yellow-400" />
          <h3 className="text-lg font-bold text-white">Leaderboards</h3>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-400" />}
      </button>

      {expanded && (
        <div className="p-4 border-t border-zinc-800">
          {/* View Mode Toggle */}
          <div className="flex bg-zinc-800/50 rounded-lg p-1 mb-4">
            <button
              onClick={() => setViewMode('performance')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                viewMode === 'performance' 
                  ? 'bg-[#02b5ff] text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Medal className="h-4 w-4" /> Performance
            </button>
            <button
              onClick={() => setViewMode('points')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                viewMode === 'points' 
                  ? 'bg-[#02b5ff] text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Star className="h-4 w-4" /> Points & Levels
            </button>
          </div>

          {viewMode === 'points' ? (
            <GamificationLeaderboard currentChildId={childId} />
          ) : (
            <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Select value={selectedTest} onValueChange={setSelectedTest}>
              <SelectTrigger className="w-48 bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="Select test" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                {tests.map(test => (
                  <SelectItem key={test.id} value={test.id} className="text-white">
                    {test.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {isCoach ? (
              <Select value={selectedAge} onValueChange={setSelectedAge}>
                <SelectTrigger className="w-32 bg-zinc-950 border-zinc-800">
                  <SelectValue placeholder="Age" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all" className="text-white">All Ages</SelectItem>
                  {[...Array(13)].map((_, i) => (
                    <SelectItem key={i + 6} value={(i + 6).toString()} className="text-white">
                      {i + 6} years
                    </SelectItem>
                  ))}
                  <SelectItem value="18" className="text-white">18+</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#02b5ff]/10 rounded text-[#02b5ff] text-sm">
                <User className="h-4 w-4" />
                Age {childAge}
              </div>
            )}
          </div>

          {/* Child's Personal Best & Rank */}
          {childRanking?.has_result && (
            <div className="bg-gradient-to-r from-[#02b5ff]/10 to-[#ff025f]/10 border border-[#02b5ff]/30 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-400">Your Best Result</p>
                  <p className="text-2xl font-bold text-white">
                    {formatResult(childRanking.best_result, childRanking.unit)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-zinc-400">Your Rank (Age {childRanking.child_age})</p>
                  <div className="flex items-center gap-2">
                    {childRanking.rank <= 3 && (
                      <span className="text-2xl">{MEDAL_COLORS[childRanking.rank]?.label}</span>
                    )}
                    <span className="text-2xl font-bold text-[#02b5ff]">
                      #{childRanking.rank}
                    </span>
                    <span className="text-sm text-zinc-500">
                      of {childRanking.total_in_age_group}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Leaderboard Table */}
          {leaderboard?.entries?.length > 0 ? (
            <div className="space-y-2">
              <div className="text-xs text-zinc-500 mb-2">
                {leaderboard.test_name} - {leaderboard.age_group}
                {leaderboard.lower_better && <span className="ml-2">(Lower is better)</span>}
              </div>
              
              {leaderboard.entries.map((entry, idx) => {
                const style = getPositionStyle(entry.rank);
                const isCurrentChild = childId && entry.child_id === childId;
                
                return (
                  <div
                    key={entry.child_id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      isCurrentChild 
                        ? 'bg-[#02b5ff]/20 border-[#02b5ff]' 
                        : `${style.bg} ${style.border}`
                    }`}
                    data-testid={`leaderboard-entry-${idx}`}
                  >
                    {/* Rank */}
                    <div className={`w-10 h-10 flex items-center justify-center rounded-full ${style.bg} border ${style.border}`}>
                      {entry.rank <= 3 ? (
                        <span className="text-xl">{style.label}</span>
                      ) : (
                        <span className={`font-bold ${style.text}`}>{entry.rank}</span>
                      )}
                    </div>

                    {/* Name & Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${isCurrentChild ? 'text-[#02b5ff]' : 'text-white'}`}>
                          {entry.child_name}
                        </span>
                        {isCurrentChild && (
                          <span className="text-xs bg-[#02b5ff] text-white px-2 py-0.5 rounded">YOU</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-zinc-500">
                        <span>Age {entry.child_age}</span>
                        {entry.child_sport && <span>{entry.child_sport}</span>}
                      </div>
                    </div>

                    {/* Result */}
                    <div className="text-right">
                      <p className={`text-lg font-bold ${entry.rank <= 3 ? style.text : 'text-white'}`}>
                        {formatResult(entry.best_result, leaderboard.unit)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <Trophy className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p>No results for this test yet</p>
              <p className="text-sm mt-1">Complete some athletic tests to appear on the leaderboard</p>
            </div>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
}
